import {
  Account,
  AudioConferencingService,
  Service,
  TicketsAware,
} from "@services/account/types";
import { Indexed } from "@services/common/types";
import { Device } from "@services/devices/types";
import { Room, RoomDetails } from "@services/rooms/types";
import { User } from "@services/user/types";

export interface MeetingsResponse {
  meetings: Meetings;
}

export interface MeetingCountersResponse {
  liveMeetings: number;
  futureMeetings: number;
  nextMeetingStartTime: number;
}

export interface Meetings {
  index?: number;
  size?: number;
  total?: number;
  list?: MeetingInstance[];
}

export interface ScheduledMeeting {
  recurrenceEnd?: number;
  externalId?: string;
  hostMcu?: ScheduledMeetingMcu;
  emailParticipants?: Array<ScheduledMeetingEmailParticipant>;
  techLineAudioService?: AudioConferencingService;
  meetingSetup?: MeetingSetup;
  readOnly?: boolean;
  scheduledMeetingRooms?: Array<ScheduledMeetingRoom>;
  exchangeId?: string;
  subject?: string;
  owner?: User;
  dialNumber?: string;
  lastUpdatedDate?: number;
  producer?: User;
  joinByWeb: boolean;
  parentMeeting?: ScheduledMeeting;
  requestorEmail?: string;
  timeZone?: string;
  ownerEmail?: string;
  audioServiceAutoSelected?: boolean;
  recorded?: boolean;
  customFieldValues?: Array<CustomFieldValue>;
  evcsMeetingId?: string;
  joinUrl?: string;
  end?: number;
  producerService?: ProducerService;
  mcuAutoSelected?: boolean;
  evcsFullMode?: boolean;
  conferenceTemplate?: string;
  createdDate?: number;
  extendedProperties?: { [key: string]: string };
  creator?: User;
  sendEmailNotification: boolean;
  setupTimeMinutes?: number;
  streamingUrl?: string;
  status?: ConferenceStatus;
  meetingService?: MeetingServiceType;
  streamed?: boolean;
  totalPorts?: number;
  reservedPorts: number;
  mcuPool?: string;
  layout?: VideoLayoutFramingDomain2;
  meetingSource?: MeetingSource;
  evcsPasscode?: string;
  meetingSyncProblem?: string;
  meetingPurpose?: MeetingPurpose;
  createVmr: boolean;
  vmrId?: string;
  accountId?: string;
  joinByPhone: boolean;
  ban?: string;
  start?: number;
  confirmationNumber?: number;
  vip?: boolean;
  notes?: string;
  mcus?: Array<ScheduledMeetingMcu>;
  recurrence?: string;
  recurrentInstanceId?: string;
  audioService?: AudioConferencingService;
  setupDate?: number;
  costCenter?: string;
  serviceProvider?: string;
  scheduledMeetingRoomsCustom?: Array<CustomRoom>;
  numOpenTickets: number;
}

export enum MeetingServiceType {
  Unknown = "Unknown",
  Self = "Self",
  Concierge = "Concierge",
}

export enum MeetingPurpose {
  RegularMeeting = "RegularMeeting",
  Maintenance = "Maintenance",
  CertificationOfManaged = "CertificationOfManaged",
  CertificationOfUnmanaged = "CertificationOfUnmanaged",
  Sweep = "Sweep",
  Other = "Other",
}
export const MeetingPurposeLabels: { [k in MeetingPurpose]: string } = {
  [MeetingPurpose.Other]: "Other",
  [MeetingPurpose.Maintenance]: "Maintenance",
  [MeetingPurpose.RegularMeeting]: "Regular Meeting",
  [MeetingPurpose.CertificationOfManaged]: "Certification Of Managed",
  [MeetingPurpose.CertificationOfUnmanaged]: "Certification Of Unmanaged",
  [MeetingPurpose.Sweep]: "Room Sweep",
};

export interface CustomRoom {
  name?: string;
  id?: number;
  protocol?: Protocol;
  extension?: string;
  dialIn?: boolean;
  meeting?: ScheduledMeeting;
  external?: boolean;
  layout?: VideoLayoutFramingDomain2;
  address?: string;
  createdDate?: number;
  userId?: string;
}

export interface VideoLayoutFramingDomain2 extends VideoLayoutFraming {
  participantIdentifier?: ParticipantIdentifier;
  id?: number;
  pinnings?: Array<VideoLayoutPinning>;
  videoLayout?: VideoLayout;
}

export enum MeetingSource {
  Symphony = "Symphony",
  Phone = "Phone",
  Email = "Email",
  Exchange = "Exchange",
  Other = "Other",
  Import = "Import",
}

export interface VideoLayoutFraming {
  videoLayout?: VideoLayout;
  participantIdentifier?: ParticipantIdentifier;
  pinnings?: Array<VideoLayoutPinning>;
}

export interface VideoLayoutPinning extends ParticipantIdentifier {
  id?: number;
  conferenceParticipantId?: number;
  position?: number;
}

export enum ProducerService {
  NoAttendance = "NoAttendance",
  GreetParticipant = "GreetParticipant",
  MonitorConnection = "MonitorConnection",
  FullAttendance = "FullAttendance",
}

export enum ProducerServiceMapping {
  NoAttendance = "None",
  GreetParticipant = "Greet Participants",
  MonitorConnection = "Monitor Connections",
  FullAttendance = "Full Attendance",
}

export interface ParticipantIdentifier {
  bridgeId?: number;
  deviceId?: string;
  externalId?: string;
  customRoomId?: number;
}

export enum ConferenceStatus {
  Scheduled = "Scheduled",
  InProgress = "InProgress",
  InProgressPartial = "InProgressPartial",
  Completed = "Completed",
  Cancelled = "Cancelled",
  Failed = "Failed",
  Deleted = "Deleted",
}

export enum VideoLayout {
  FullScreenVAS = <any>"Full Screen VAS",
  OneByTwo = <any>"One By Two",
  FourByQuad = <any>"Four By Quad",
  FourByQuadRightFlex = <any>"Four By Quad Right Flex",
  FourByQuadLeftFlex = <any>"Four By Quad Left Flex",
  FourByQuadUpperRightFlex = <any>"Four By Quad Upper Right Flex",
  FourByQuadUpperLeftFlex = <any>"Four By Quad Upper Left Flex",
  FourByQuadDownRightFlex = <any>"Four By Quad Down Right Flex",
  FourByQuadDownLeftFlex = <any>"Four By Quad Down Left Flex",
  SixteenWayFourByFour = <any>"Sixteen Way Four By Four",
  TwentyWayFiveByFour = <any>"Twenty Way Five By Four",
  NineWay = <any>"Nine Way",
  TwoByOne = <any>"Two By One",
  OneByTwoFlex = <any>"One By Two Flex",
  TwoByHorizontal = <any>"Two By Horizontal",
  EightByTwo = <any>"Eight By Two",
  OneUpperLeftFourRightAndFourBelow = <any>(
    "One Upper Left Four Right And Four Below"
  ),
  TwoUpperEightBottom = <any>"Two Upper Eight Bottom",
  TwoByVertical = <any>"Two By Vertical",
  TwoByOneUpperPanoramic = <any>"Two By One Upper Panoramic",
  TwoByOneLowerPanoramicLeftFlex = <any>"Two By One Lower Panoramic Left Flex",
  TwoByOneLowerPanoramicRightFlex = <any>(
    "Two By One Lower Panoramic Right Flex"
  ),
  TwoByOneUpperPanoramicLeftFlex = <any>"Two By One Upper Panoramic Left Flex",
  TwoByOneUpperPanoramicRightFlex = <any>(
    "Two By One Upper Panoramic Right Flex"
  ),
  TwoByOneLowerPanoramic = <any>"Two By One Lower Panoramic",
  OneByTwoVertical = <any>"One By Two Vertical",
  ThreeByOneUpperPanoramic = <any>"Three By One Upper Panoramic",
  ThreeByOneLowerPanoramic = <any>"Three By One Lower Panoramic",
  OneAndThreeVertical = <any>"One And Three Vertical",
  FourByOneLowerPanoramic = <any>"Four By One Lower Panoramic",
  FourByOneUpperPanoramic = <any>"Four By One Upper Panoramic",
  OneByFourVertical = <any>"One By Four Vertical",
  FivePlusOne = <any>"Five Plus One",
  SevenPlusOne = <any>"Seven Plus One",
  EightByOne = <any>"Eight By One",
  TwelveByOne = <any>"Twelve By One",
  EightByOneLowerPanoramic = <any>"Eight By One Lower Panoramic",
  EightByOneUpperPanoramic = <any>"Eight By One Upper Panoramic",
  OneAndOneWithOverlay = <any>"One And One With Overlay",
  OneAndTwoWithOverlay = <any>"One And Two With Overlay",
  OneAndThreeWithOverlay = <any>"One And Three With Overlay",
  OneAndTwoWithITPOverlay = <any>"One And Two With ITP Overlay",
  Automatic = <any>"Automatic",
  AllEqual = <any>"All Equal",
  SpeakerOnly = <any>"Speaker Only",
  Telepresence = <any>"Telepresence",
  Stacked = <any>"Stacked",
  AllEqualQuarters = <any>"All Equal Quarters",
  AllEqualNinths = <any>"All Equal Ninths",
  AllEqualSixteenths = <any>"All Equal Sixteenths",
  AllEqualTwentyFifths = <any>"All Equal Twenty Fifths",
  OnePlusFive = <any>"One Plus Five",
  OnePlusSeven = <any>"One Plus Seven",
  OnePlusNine = <any>"One Plus Nine",
  OnePlusN = <any>"One Plus N",
  MainSpeakerPlusSeven = <any>"Main Speaker Plus Seven",
  MainSpeakerPlusTwentyOne = <any>"Main Speaker Plus Twenty One",
  TwoMainSpeakersPlusTwentyOne = <any>"Two Main Speakers Plus Twenty One",
}

export interface ScheduledMeetingMcu {
  device?: Device;
  mcuReservationId?: string;
  id?: number;
  meeting?: ScheduledMeeting;
}

export interface RoomAccountVisibility {
  id?: RoomAccountVisibilityId;
  accountId?: string;
  roomDetails?: RoomDetails;
}

export interface RoomAccountVisibilityId {
  accountId?: string;
  roomId?: string;
}

export interface ControllableType {
  _class?: string;
}

export interface XmlNs0PauseMonitoringOption {}

export interface Statistics {
  _class?: string;
}

export interface XmlNs0PauseMonitoringStatus
  extends XmlNs0PauseMonitoringOption {
  outOfSchedule?: boolean;
  outOfScheduleUntil?: string;
  parentPaused?: boolean;
}

export interface ScheduledMeetingEmailParticipant {
  meeting?: ScheduledMeeting;
  id?: number;
  email?: string;
  name?: string;
}

export interface XmlNs0PauseMonitoringStatus
  extends XmlNs0PauseMonitoringOption {
  outOfSchedule?: boolean;
  outOfScheduleUntil?: string;
  parentPaused?: boolean;
}

export interface UserServiceId {
  service?: Service;
  userId?: string;
}

export interface UserService {
  id?: UserServiceId;
  user?: User;
  service?: Service;
}

export enum AccessLevel {
  NoAccess = <any>"NoAccess",
  ViewOnly = <any>"ViewOnly",
  FullAccess = <any>"FullAccess",
}

export interface UserDetails {
  billingCode?: string;
  encryptedModerationCode?: string;
  pendingSelfActivation?: boolean;
  notes?: string;
  country?: string;
  passwordSalt?: string;
  mobile?: string;
  selfRegistered?: boolean;
  theme?: string;
  preferredLanguage?: string;
  pendingWelcomeEmail?: boolean;
  moderationCode?: string;
  department?: string;
  deviceWatchList?: Array<string>;
  id?: string;
  city?: string;
  postalCode?: string;
  registrationToken?: string;
  phone?: string;
  createdDate?: number;
  userName?: string;
  user?: User;
  visibleAccountIds?: Array<string>;
  timeZone?: string;
  address1?: string;
  title?: string;
  supervisor?: string;
  lastUpdatedDate?: number;
  address2?: string;
  registrationDate?: number;
  stateOrProvince?: string;
  password?: string;
  useMetricSystem: boolean;
}

export interface CustomFieldValue {
  value?: string;
  meeting?: ScheduledMeeting;
  customFieldId?: number;
  id: number;
}

export enum MeetingSetup {
  Automatic = <any>"Automatic",
  Manual = <any>"Manual",
}

export interface ScheduledMeetingRoom {
  hostRoom?: boolean;
  released?: boolean;
  dialIn: boolean;
  room?: Room;
  createdDate?: number;
  meetingRoomDeviceLayouts?: Array<ScheduledMeetingRoomDeviceLayout>;
  ban?: string;
  meeting?: ScheduledMeeting;
  id?: number;
  response?: RoomResponse;
}

export interface ScheduledMeetingRoomDeviceLayout {
  meetingRoom?: ScheduledMeetingRoom;
  deviceId?: string;
  layout?: VideoLayoutFramingDomain2;
  id?: number;
}

export enum RoomResponse {
  NONE = "NONE",
  TENTATIVELYACCEPTED = "TENTATIVELY_ACCEPTED",
  ACCEPTED = "ACCEPTED",
  DECLINED = "DECLINED",
}

export enum ChannelType {
  Audio = "Audio",
  Video = "Video",
}

export enum ChannelDirection {
  In = "In",
  Out = "Out",
}

export type IndexedMeetingInstance = MeetingInstance & Indexed;
export interface MeetingInstance extends TicketsAware {
  start?: number;
  evcsFullMode?: boolean;
  totalConnectedParticipants?: number;
  scheduledMeeting?: ScheduledMeeting;
  wrappedUp?: boolean;
  setupTime?: number;
  serviceDetails?: Array<MeetingInstanceService>;
  layout?: VideoLayoutFramingDomain2;
  participants?: Array<ConferenceParticipant>;
  account?: Account;
  producerAssigned?: boolean;
  mcuType?: McuType;
  recordingEnabled?: boolean;
  type?: MeetingType;
  id?: number;
  meetingId?: number;
  vmrId?: string;
  locked?: boolean;
  producer?: User;
  services?: Array<Service>;
  notes?: string;
  end?: number;
  statusDetails?: string;
  title?: string;
  recordingId?: number;
  recordingIds?: Array<number>;
  sipUri?: string;
  status?: ConferenceStatus;
  recording?: Recording;
  meetingLastUpdatedTime?: number;
  monitoringOnly?: boolean;
  vip?: boolean;
}

export interface Recording {
  id?: number;
  url?: string;
  tags?: Array<string>;
  status?: RecordingStatus;
  startTimeSynchronized: boolean;
  published: boolean;
  recorderUri?: string;
  deletedDate?: number;
  inProgress: boolean;
  owner?: User;
  listed: boolean;
  pin?: string;
  duration?: number;
  meetingUri?: string;
  description?: string;
  sessionId?: string;
  recorderId?: string;
  account?: Account;
  encryptedPassword?: string;
  paused: boolean;
  publicRecording: boolean;
  title?: string;
  storageProvider?: RecordingStorageProvider;
  recordingStart?: number;
  fileSize?: number;
  fileName?: string;
  posterUrl?: string;
}

export interface ConferenceParticipant {
  videoMuteRx?: boolean;
  disconnectTime?: number;
  customRoomId?: number;
  connectTime?: number;
  remoteAddress?: string;
  contentSharing?: boolean;
  name?: string;
  status?: ConnectionState;
  external?: boolean;
  extension?: string;
  protocol?: string;
  notes?: string;
  released?: boolean;
  id?: number;
  layout?: VideoLayoutFramingDomain2;
  videoMuteTx?: boolean;
  dialIn?: boolean;
  callId?: string;
  address?: string;
  statusDetails?: string;
  externalId?: string;
  audioMuteTx?: boolean;
  meetingInstance?: MeetingInstance;
  scheduled?: boolean;
  room?: Room;
  device?: Device;
  bandwidth?: number;
  audioMuteRx?: boolean;
}

export interface MeetingInstanceService {
  id?: MeetingInstanceServiceId;
  service?: Service;
  meetingInstance?: MeetingInstance;
}

export interface MeetingInstanceServiceId {
  service?: Service;
  meetingInstanceId?: number;
}

export enum Protocol {
  PSTN = "PSTN",
  H323 = "H323",
  SIP = "SIP",
  H320 = "H320",
}

export enum McuType {
  RMX = <any>"RMX",
  CMS = <any>"CMS",
  Pexip = <any>"Pexip",
  DMA = <any>"DMA",
}

export enum MeetingType {
  RoomOnly = "RoomOnly",
  PointToPoint = "PointToPoint",
  MultiPoint = "MultiPoint",
  AdHoc = "AdHoc",
  VMR = "VMR",
}

export const MeetingTypeLabels: { [k in MeetingType]: string } = {
  [MeetingType.RoomOnly]: "Room Only",
  [MeetingType.PointToPoint]: "Point To Point",
  [MeetingType.MultiPoint]: "MultiPoint",
  [MeetingType.AdHoc]: "Ad Hoc",
  [MeetingType.VMR]: "EVCS",
};

export enum RecordingStatus {
  InProgress = <any>"InProgress",
  Finished = <any>"Finished",
  Completed = <any>"Completed",
  Failed = <any>"Failed",
  Deleted = <any>"Deleted",
}

export enum RecordingStorageProvider {
  Repository = <any>"Repository",
  Service = <any>"Service",
}

export enum ConnectionState {
  Unknown = "Unknown",
  Connecting = "Connecting",
  Connected = "Connected",
  Disconnected = "Disconnected",
  Failed = "Failed",
  PartiallyConnected = "PartiallyConnected",
}

export const ConnectionStateLabels: { [k in ConnectionState]: string } = {
  [ConnectionState.Unknown]: "Unknown",
  [ConnectionState.Connecting]: "Connecting",
  [ConnectionState.Connected]: "Connected",
  [ConnectionState.Disconnected]: "Disconnected",
  [ConnectionState.Failed]: "Failed",
  [ConnectionState.PartiallyConnected]: "Partially Connected",
};

export type MeetingsCount = number;

export interface MeetingsFilters {
  accountId?: string[];
  scheduledOnly?: boolean;
  recurrentOnly?: boolean;
  loggedInProducerOnly?: boolean;
  status?: string[];
  includeAllInProgress?: boolean;
}

export type Period = { after: string; before: string };

export type ParticipantFilter = {
  device?: {
    name?: string;
    id?: string;
  };
  room?: {
    name?: string;
    location?: {
      name?: string;
    };
    id?: string;
  };
};

export enum ScheduledMeetingProducerService {
  FullAttendance = "FullAttendance",
  GreetParticipant = "GreetParticipant",
  MonitorConnection = "MonitorConnection",
}

export const ScheduledMeetingProducerServiceLabels: {
  [k in ScheduledMeetingProducerService]: string;
} = {
  [ScheduledMeetingProducerService.FullAttendance]: "Full Attendance",
  [ScheduledMeetingProducerService.GreetParticipant]: "Greet Participants",
  [ScheduledMeetingProducerService.MonitorConnection]: "Monitor Connections",
};

export interface MeetingInstanceFilters {
  accountId?: string[];
  meetingId?: string[];
  vmrId?: string[];
  producerIds?: string[];
  loggedInProducerOnly?: boolean;
  producerSearchType?: string;
  managedOnly?: boolean;
  includeAllInProgress?: boolean;
  status?: string[];
  type?: string[];
  watchList?: boolean;
  purpose?: string[];
  includeAllWithOpenTickets?: boolean;
  "participants.room.name"?: string;
  "participants.room.id"?: string;
  "participants.room.location.name"?: string;
  "participants.room.location.id"?: string;
  vip?: boolean;
  "scheduledMeeting.meetingSource"?: string[];
  "scheduledMeeting.producerService"?: ScheduledMeetingProducerService;
  "scheduledMeeting.requestorEmail"?: string;
  "scheduledMeeting.extendedProperties.EMSID"?: string;
  "scheduledMeeting.emailParticipants.email"?: string;
  "scheduledMeeting.evcsMeetingId"?: string;
  "scheduledMeeting.ownerEmail"?: string;
  wrappedUp?: boolean;
  "start.after"?: number;
  "end.before"?: number;
  title?: string;
}

export interface ElogBillingCode {
  id: number;
  quantity: number;
  billingCode: string;
  description: string;
}
export interface ElogBillingEntry
  extends Pick<
    ElogBillingCode,
    "id" | "quantity" | "billingCode" | "description"
  > {
  roomName?: string;
}
export interface ElogRoom
  extends Pick<
    Room,
    | "id"
    | "name"
    | "inService"
    | "managed"
    | "schedulingOption"
    | "vip"
    | "hasUnProvisionedDevice"
    | "location"
    | "capacity"
  > {
  summary: null;
}
export interface ElogDevice
  extends Omit<Device, "status" | "room" | "details" | "aggregator"> {
  room: ElogRoom;
}
export interface ElogParticipant {
  id: number;
  participantId: number;
  address?: string;
  name: string;
  protocol: Protocol;
  dialIn: boolean;
  bandwidth?: number;
  connectTime: number;
  disconnectTime?: number;
  // There's either a room + device or an externalId
  room?: ElogRoom;
  device?: ElogDevice;
  externalId?: number;
  /**
   * Couldn't find a request where the participant had these, but they are used in SY5's code
   */
  billingCode?: string;
  description?: string;
  quantity?: number;
}

/**
 * From the old code:
 * If there's a name, it takes priority of the room.name
 */
export interface ElogAttendance {
  id: number;
  name?: string;
  room: ElogRoom;
  actualAttendance?: number;
}
export interface ElogUser extends Omit<User, "serviceDetails" | "details"> {}
export interface ElogDetail {
  meetingServiceType?: MeetingServiceType;
  purpose?: MeetingPurpose;
  source?: MeetingSource;
  meetingCreator?: ElogUser;
  producer?: ElogUser;
  mcu?: ElogDevice;
  recorded?: boolean;
  streamed?: boolean;
  vipMeeting?: boolean;
  produced?: boolean;
  meetingOwner?: string;
  meetingRequester?: string;
  qualityAssessment?: number;
  succeeded?: boolean;
  producerAttendedFullMeeting?: boolean;
  producerMonitoredConnections?: boolean;
  producerAttendanceMinutes?: number;
  overtime?: boolean;
  thirdPartyTickets?: string;
}
export interface MeetingElog {
  meetingInstanceId: MeetingInstance["id"];
  meetingId: MeetingInstance["meetingId"];
  wrappedUp?: MeetingInstance["wrappedUp"];
  note?: string;
  creator: string;
  elogDetail?: ElogDetail;
  elogParticipants?: ElogParticipant[];
  elogBillingCodes?: ElogBillingCode[];
  elogAttendances?: ElogAttendance[];
}

export interface MeetingRevision {
  billable: boolean;
  created: number;
  creator?: string;
  description: string;
  id: number;
  meetingId: number;
}

export enum MeetingMessageColor {
  "white_font_on_light_blue_background" = "white_font_on_light_blue_background",
  "white_font_on_black_background" = "white_font_on_black_background",
  "white_font_on_gray_background" = "white_font_on_gray_background",
  "white_font_on_red_background" = "white_font_on_red_background",
  "white_font_on_orange_background" = "white_font_on_orange_background",
  "white_font_on_blue_background" = "white_font_on_blue_background",
  "white_font_on_olive_background" = "white_font_on_olive_background",
  "white_font_on_green_background" = "white_font_on_green_background",
  "white_font_on_purple_background" = "white_font_on_purple_background",
  "red_font_on_white_background" = "red_font_on_white_background",
  "white_font_on_deep_brown_background" = "white_font_on_deep_brown_background",
  "white_font_on_brown_background" = "white_font_on_brown_background",
  "yellow_font_on_black_background" = "yellow_font_on_black_background",
  "yellow_font_on_deep_blue_background" = "yellow_font_on_deep_blue_background",
  "lightblue_font_on_black_background" = "lightblue_font_on_black_background",
  "blue_font_on_white_background" = "blue_font_on_white_background",
  "green_font_on_black_background" = "green_font_on_black_background",
  "greygreen_font_on_white_background" = "greygreen_font_on_white_background",
  "black_font_on_gray_background" = "black_font_on_gray_background",
  "black_font_on_white_background" = "black_font_on_white_background",
}
export enum MeetingMessageFontSize {
  "Small" = "Small",
  "Medium" = "Medium",
  "Large" = "Large",
}
export enum MeetingMessageSpeed {
  "Slow" = "Slow",
  "Fast" = "Fast",
  "Static" = "Static",
}
export enum MeetingMessagePosition {
  "Top" = "Top",
  "Middle" = "Middle",
  "Bottom" = "Bottom",
}
export interface MeetingMessage {
  message: string;
  color: MeetingMessageColor;
  fontSize: MeetingMessageFontSize;
  repetitions: number;
  speed: MeetingMessageSpeed;
  position: MeetingMessagePosition;
  transparency: number;
}
export interface ConsultedRooms {
  external: Array<CustomRoom>;
  internal: Array<Room>;
}

export interface Infrastructure {
  gateway: ElogDevice[];
  mcu: ElogDevice[];
}
